import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import { TApi } from '../../../../shared/const'
import { stateFullLabel } from '../../../../shared/helpers'
import http from '../../../../shared/http'
import useAttentionPulse, { PULS_KLASSE } from '../../../../shared/useAttentionPulse'

import styles from './stats.module.scss'

/**
 * Wie es um die Anfragen über den Hilfe-Knopf steht — je Bundesland
 * (Anfragen-Konzept, Stufe 4).
 *
 * Der Kanal ist nur so gut wie die Antwort darauf. Drei Zahlen und eine Zeit
 * beantworten das: Wie viele kamen, wie viele sind offen, wie viele wurden
 * verworfen — und **wie lange es bis zur ersten sichtbaren Antwort dauert**.
 * Die letzte ist die eigentliche: Alles andere misst unsere Ablage, nicht die
 * Erfahrung der meldenden Person.
 *
 * Der Block erscheint nur, wenn es überhaupt Anfragen gibt. Eine Tabelle mit
 * neun Nullzeilen sagt nichts und nimmt den ganzen Bildschirm.
 */
export default function RequestStats({ onPick, activeState, reloadKey, eigenesLand }) {
	const [rows, setRows] = useState(null)

	/*
	 * Die Zeile der eigenen Landesadministration steht hervorgehoben, und ihre
	 * offenen Anfragen leuchten beim Anzeigen kurz auf: In einer Tabelle mit
	 * neun gleich aussehenden Zeilen ist die eigene nicht zu finden, ohne sie
	 * zu suchen — dabei ist sie die einzige, für die man zuständig ist.
	 *
	 * Nur, wenn wirklich etwas offen ist. Eine leuchtende Null ruft nach einer
	 * Handlung, die es nicht gibt.
	 *
	 * Der Hook steht hier oben und nicht in der Schleife — Hooks gehören nicht
	 * in eine `map`. Es gibt ohnehin genau eine eigene Zeile.
	 */
	const eigeneZeile = eigenesLand && rows?.find(zeile => zeile.state === eigenesLand)
	const puls = useAttentionPulse(Boolean(eigeneZeile?.open))

	/*
	 * `reloadKey` zaehlt die Aenderungen an den Anforderungen mit. Ohne ihn
	 * stand hier der Stand vom Oeffnen der Seite: Eine Anfrage, die gerade
	 * abgeschlossen wurde, blieb in der Zeile ihres Bundeslandes als "offen"
	 * stehen, waehrend Liste und Trichter daneben schon stimmten.
	 */
	useEffect(() => {
		let aktuell = true

		http.get(TApi.ADMIN_TICKET_REQUEST_STATS, { disableToast: true })
			.then(({ data }) => {
				if (aktuell) setRows(data.rows || [])
			})
			// Eine fehlende Auswertung ist kein Grund, die Liste darunter zu
			// stören — sie bleibt dann einfach weg.
			.catch(() => aktuell && setRows([]))

		return () => {
			aktuell = false
		}
	}, [reloadKey])

	if (!rows?.length) return null

	const stunden = wert => {
		if (wert === null || wert === undefined) return '—'
		if (wert < 24) return `${Math.round(wert)} h`
		return `${Math.round(wert / 24)} d`
	}

	return (
		<section className={styles.root}>
			{/*
			 * Nur eine Überschrift, kein Klapp-Knopf: Der Block sitzt seit
			 * 24.08.2026 im Auswertungsblock der Anforderungsliste, und der
			 * klappt zu. Zwei Klappen ineinander sind eine zu viel — man weiß
			 * dann nicht mehr, welche gerade zugeklappt ist.
			 */}
			<div className={styles.ueberschrift}>
				<span className={styles.titel}>Anfragen über den Hilfe-Knopf</span>
				<span className={styles.summe}>
					{rows.reduce((summe, zeile) => summe + zeile.open, 0)} offen von{' '}
					{rows.reduce((summe, zeile) => summe + zeile.total, 0)}
				</span>
			</div>

			<p className={styles.unterzeile}>
				Je Bundesland: was eingegangen ist, was offen steht — und wie lange es bis zur ersten sichtbaren Antwort
				dauert (Median). Eine Zeile schränkt die Liste darunter auf dieses Bundesland ein.
			</p>

			<div className={styles.tabelle}>
				<div className={styles.kopf}>
					<span>Bundesland</span>
					<span className={styles.zahl}>Eingegangen</span>
					<span className={styles.zahl}>Offen</span>
					<span className={styles.zahl}>Beantwortet</span>
					<span className={styles.zahl}>1. Antwort</span>
					<span className={styles.zahl}>Verworfen</span>
					<span className={styles.zahl}>Archiv</span>
				</div>

				{rows.map(zeile => {
					// „0" ist wie überall in den Filtern das „ohne" — hier
					// also bundesweit.
					const wert = zeile.state || '0'
					const aktiv = activeState.includes(wert)
					const eigen = Boolean(eigenesLand) && zeile.state === eigenesLand

					return (
						<button
							type="button"
							className={clsx(styles.zeile, aktiv && styles.zeileAktiv, eigen && styles.zeileEigen)}
							key={wert}
							aria-pressed={aktiv}
							title={[
								eigen ? `Ihr Bundesland: ${stateFullLabel(zeile.state)}` : null,
								aktiv
									? 'Einschränkung auf dieses Bundesland aufheben'
									: 'Liste auf dieses Bundesland einschränken'
							]
								.filter(Boolean)
								.join(' — ')}
							onClick={() => onPick(wert)}
						>
							{/*
							 * Ausgeschrieben, nicht abgekürzt: Die Tabelle hat
							 * eine eigene Spalte dafür und neun Zeilen Platz.
							 * „Vbg" und „Bgld" nebeneinander liest niemand
							 * flüssig, und die Zeile ist ein Filterknopf — man
							 * muss wissen, worauf man die Liste einschränkt.
							 */}
							<span className={styles.land}>
								{zeile.state ? stateFullLabel(zeile.state) : 'Bundesweit'}
							</span>
							<span className={styles.zahl}>{zeile.total}</span>
							<span className={clsx(styles.zahl, eigen && styles.zahlEigen)}>
								{eigen && zeile.open > 0 ? (
									<span
										className={clsx(styles.offenMarke, puls.leuchtet && PULS_KLASSE)}
										onAnimationEnd={puls.onAnimationEnd}
									>
										{zeile.open}
									</span>
								) : (
									zeile.open
								)}
							</span>
							<span className={styles.zahl}>{zeile.answered}</span>
							<span className={styles.zahl}>{stunden(zeile.medianReactionHours)}</span>
							<span className={styles.zahl}>{zeile.rejected}</span>
							<span className={styles.zahl}>{zeile.archived}</span>
						</button>
					)
				})}
			</div>
		</section>
	)
}

RequestStats.propTypes = {
	// Klick auf eine Zeile: schaltet dieses Bundesland im Filter an oder aus.
	onPick: PropTypes.func.isRequired,
	// Welche Bundesländer gerade gefiltert sind — die Zeile zeigt es an.
	activeState: PropTypes.array,
	// Zaehlt hoch, sobald sich an den Anforderungen etwas geaendert hat: Die
	// Auswertung holt sich die Zahlen dann neu.
	reloadKey: PropTypes.number,
	// Bundesland der angemeldeten Person — nur eine Landesadministration hat
	// eines. Ihre Zeile steht hervorgehoben.
	eigenesLand: PropTypes.string
}

RequestStats.defaultProps = {
	activeState: [],
	reloadKey: 0
}
